import { localDayKey, type Streaks } from "@versedrill/core";

export const DEFAULT_REMINDER_HOUR = 8;
/** Hours offered in the picker: early enough for a commute, late enough for a bedtime verse. */
export const REMINDER_HOURS = Array.from({ length: 18 }, (_, i) => i + 5); // 5 AM … 10 PM

export type ReminderAvailability = "unavailable" | "install-first" | "blocked" | "ready";

/**
 * What the reminder card should offer on this device.
 *  - unavailable:   the server has no push key, or this browser can't do push at all
 *  - install-first: iPhone/iPad only deliver push to an app on the home screen, so ask for that first
 *  - blocked:       the player (or their browser) said no to notifications
 */
export function reminderAvailability(o: { serverEnabled: boolean; hasPushApi: boolean; ios: boolean; standalone: boolean; permission: NotificationPermission | "unsupported" }): ReminderAvailability {
  if (!o.serverEnabled) return "unavailable";
  if (o.ios && !o.standalone) return "install-first";
  if (!o.hasPushApi) return "unavailable";
  if (o.permission === "denied") return "blocked";
  return "ready";
}

/** The player's own calendar day of their last completed game. (`currentStreakStart` is the time of the latest completion.) */
export const lastPlayedDay = (s: Streaks): string | null => (s.currentStreakStart ? localDayKey(new Date(s.currentStreakStart)) : null);

export const hourLabel = (hour: number, locale?: string): string => new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(new Date(2000, 0, 1, hour, 0));

/** The browser wants the server's public key as bytes. */
export function keyToBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64url.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

/** What the service worker needs to word tonight's notification. It lives in Cache Storage because workers can't read localStorage. */
export const REMINDER_STATE_CACHE = "state:ptv";
export const REMINDER_STATE_KEY = "/__reminder-state";
export async function mirrorReminderState(streaks: Streaks): Promise<void> {
  try {
    const cache = await caches.open(REMINDER_STATE_CACHE);
    await cache.put(REMINDER_STATE_KEY, new Response(JSON.stringify({ streak: streaks.currentStreak, lastPlayed: lastPlayedDay(streaks) }), { headers: { "content-type": "application/json" } }));
  } catch {
    /* the notification just uses its plain wording */
  }
}
